import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { FormValues, Participant, Device } from "../utils/meeting.utils";
import { format } from "date-fns";

interface MeetingFormState {
  formData: Partial<FormValues>;
  isLoading: boolean;
  errors: Partial<Record<keyof FormValues, string>>;
}

// Helper function để tính toán default values dựa trên thời gian hiện tại
const getDefaultFormData = (firstRoomId?: string): Partial<FormValues> => {
  const now = new Date();
  const currentDate = new Date(now);
  
  // Format ngày: DD/MM/YYYY
  const dateStr = format(currentDate, "dd/MM/yyyy");
  
  // Tính giờ bắt đầu: giờ hiện tại + 5 phút
  const startTime = new Date(now);
  startTime.setMinutes(startTime.getMinutes() + 5);
  const startTimeStr = `${String(startTime.getHours()).padStart(2, "0")}:${String(startTime.getMinutes()).padStart(2, "0")}`;
  
  // Tính giờ kết thúc: giờ bắt đầu + 1 tiếng
  const endTime = new Date(startTime);
  endTime.setHours(endTime.getHours() + 1);
  const endTimeStr = `${String(endTime.getHours()).padStart(2, "0")}:${String(endTime.getMinutes()).padStart(2, "0")}`;
  
  // Format ngày kết thúc: yyyy-MM-dd
  const recurrenceEndDateStr = format(currentDate, "yyyy-MM-dd");
  
  return {
    title: `Họp - ${dateStr}`,
    description: `Họp - ${dateStr}`,
    date: currentDate,
    startTime: startTimeStr,
    endTime: endTimeStr,
    roomMeetingId: firstRoomId,
    participantHost: undefined,
    participants: [],
    devices: [],
    allowRecording: false,
    autoRecord: false,
    defaultMicOn: false,
    defaultCameraOn: false,
    cameraResolution: "1080p",
    waitingRoomEnable: false,
    requirePassword: false,
    password: "",
    maxParticipants: 10,
    isRecurring: false,
    recurrencePattern: undefined,
    recurrenceDays: [],
    recurrenceEndDate: recurrenceEndDateStr,
    notes: "",
    reminderMinutes: 10,
    type: "scheduled",
  };
};

const defaultFormData: Partial<FormValues> = {
  title: "",
  description: "",
  date: new Date(),
  startTime: "09:00",
  endTime: "10:00",
  roomMeetingId: undefined,
  participantHost: undefined,
  participants: [],
  devices: [],
  allowRecording: false,
  autoRecord: false,
  defaultMicOn: false,
  defaultCameraOn: false,
  cameraResolution: "1080p",
  waitingRoomEnable: false,
  requirePassword: false,
  password: "",
  maxParticipants: 10,
  isRecurring: false,
  recurrencePattern: undefined,
  recurrenceDays: [],
  recurrenceEndDate: undefined,
  notes: "",
  reminderMinutes: 10,
  type: "scheduled",
};

const initialState: MeetingFormState = {
  formData: defaultFormData,
  isLoading: false,
  errors: {},
};

const meetingFormSlice = createSlice({
  name: "meetingForm",
  initialState,
  reducers: {
    // Reset form về giá trị mặc định
    resetFormData(state) {
      state.formData = defaultFormData;
      state.errors = {};
    },

    // Initialize form với default values động
    initializeDefaultFormData(state, action: PayloadAction<{ firstRoomId?: string }>) {
      const firstRoomId = action.payload?.firstRoomId;
      state.formData = getDefaultFormData(firstRoomId);
      state.errors = {};
    },

    // Cập nhật title
    setTitle(state, action: PayloadAction<string>) {
      state.formData.title = action.payload;
      delete state.errors.title;
    },

    // Cập nhật description
    setDescription(state, action: PayloadAction<string | undefined>) {
      state.formData.description = action.payload;
      delete state.errors.description;
    },

    // Cập nhật date
    setDate(state, action: PayloadAction<Date>) {
      state.formData.date = action.payload;
      delete state.errors.date;
    },

    // Cập nhật startTime
    setStartTime(state, action: PayloadAction<string>) {
      state.formData.startTime = action.payload;
      delete state.errors.startTime;
    },

    // Cập nhật endTime
    setEndTime(state, action: PayloadAction<string>) {
      state.formData.endTime = action.payload;
      delete state.errors.endTime;
    },

    // Cập nhật roomMeetingId
    setRoomMeetingId(state, action: PayloadAction<string | undefined>) {
      state.formData.roomMeetingId = action.payload;
      delete state.errors.roomMeetingId;
    },

    // Cập nhật participantHost
    setParticipantHost(state, action: PayloadAction<Participant | undefined>) {
      state.formData.participantHost = action.payload;
      delete state.errors.participantHost;
    },

    // Cập nhật participants
    setParticipants(state, action: PayloadAction<Participant[]>) {
      state.formData.participants = action.payload;
      delete state.errors.participants;
    },

    // Cập nhật devices
    setDevices(state, action: PayloadAction<Device[]>) {
      state.formData.devices = action.payload;
      delete state.errors.devices;
    },

    // Cập nhật allowRecording
    setAllowRecording(state, action: PayloadAction<boolean>) {
      state.formData.allowRecording = action.payload;
    },

    // Cập nhật autoRecord
    setAutoRecord(state, action: PayloadAction<boolean>) {
      state.formData.autoRecord = action.payload;
    },

    // Cập nhật defaultMicOn
    setDefaultMicOn(state, action: PayloadAction<boolean>) {
      state.formData.defaultMicOn = action.payload;
    },

    // Cập nhật defaultCameraOn
    setDefaultCameraOn(state, action: PayloadAction<boolean>) {
      state.formData.defaultCameraOn = action.payload;
    },

    // Cập nhật cameraResolution
    setCameraResolution(
      state,
      action: PayloadAction<"720p" | "1080p" | "4K" | undefined>
    ) {
      state.formData.cameraResolution = action.payload;
    },

    // Cập nhật waitingRoomEnable
    setWaitingRoomEnable(state, action: PayloadAction<boolean>) {
      state.formData.waitingRoomEnable = action.payload;
    },

    // Cập nhật requirePassword
    setRequirePassword(state, action: PayloadAction<boolean>) {
      state.formData.requirePassword = action.payload;
    },

    // Cập nhật password
    setPassword(state, action: PayloadAction<string | undefined>) {
      state.formData.password = action.payload;
      delete state.errors.password;
    },

    // Cập nhật maxParticipants
    setMaxParticipants(state, action: PayloadAction<number | undefined>) {
      state.formData.maxParticipants = action.payload;
      delete state.errors.maxParticipants;
    },

    // Cập nhật isRecurring
    setIsRecurring(state, action: PayloadAction<boolean>) {
      state.formData.isRecurring = action.payload;
    },

    // Cập nhật recurrencePattern
    setRecurrencePattern(
      state,
      action: PayloadAction<"daily" | "weekly" | "monthly" | "custom" | undefined>
    ) {
      state.formData.recurrencePattern = action.payload;
    },

    // Cập nhật recurrenceEndDate
    setRecurrenceEndDate(state, action: PayloadAction<string | undefined>) {
      state.formData.recurrenceEndDate = action.payload;
    },

    // Cập nhật reminderMinutes
    setReminderMinutes(state, action: PayloadAction<number | undefined>) {
      state.formData.reminderMinutes = action.payload;
    },

    // Set loading state
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    // Set errors
    setErrors(state, action: PayloadAction<Partial<Record<keyof FormValues, string>>>) {
      state.errors = { ...state.errors, ...action.payload };
    },
  },
});

export const meetingFormActions = meetingFormSlice.actions;
export const meetingFormReducer = meetingFormSlice.reducer;

